/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { useContext, useMemo } from 'react';
import { FormGroup, Input } from 'reactstrap';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { updateFn } from '../../form/common';
import { RootState, useAppSelector } from '../../../reducers';
import { SurveyContext } from '../../survey-container/SurveyContainer';
import { InitiativeMin } from '../../../types/initiative';
import { SurveyActionData } from '../../../model/surveyData';
import { ERRORS } from '../SurveyConfiguration';
import { SurveyType } from '../../../types/survey';
import { Blueprints, ConfigFormData } from '../types';
import { ReactSelectOptions } from '../../survey/form/input/selectUtils';
import { isStaff } from '../../../selectors/user';
import { getCheckOptions } from '../../../utils/survey';
import { SurveyQuestionForm } from './SurveyQuestionForm';
import { OnChangeReportingProps, SurveyReportingSettings } from './SurveyReportingSettings';
import { FeaturePermissions } from '../../../services/permissions/FeaturePermissions';

function getSurveyVersionOptions() {
  return [
    {
      label: 'Survey 2020',
      value: Blueprints.Gri2020
    }, {
      label: 'Survey 2022',
      value: Blueprints.Blueprint2022
    }
  ]
}

const getSurveyName = (surveyData?: Partial<Pick<SurveyActionData, 'name'>>, initiative?: Pick<InitiativeMin, 'name'>) => {
  if (surveyData?.name) {
    return surveyData.name;
  }
  if (initiative?.name) {
    return initiative?.name;
  }
  return 'Unnamed survey';
}

interface SurveySettingsProps {
  updateForm: updateFn;
  form: ConfigFormData;
  errorState: ERRORS;
}

export const SurveySettings = (props: SurveySettingsProps) => {
  const { updateForm, form, errorState } = props;
  const { surveyData, disabled } = useContext(SurveyContext);

  const isUserStaff = useAppSelector(isStaff);
  const isDisabled = Boolean(surveyData.completedDate) || disabled;

  const initiativeState = useSelector((state: RootState) => state.initiative);
  const initiative = initiativeState.data;

  const surveyVersionOptions = getSurveyVersionOptions();
  const selectedSurveyVersion = surveyVersionOptions.find((o: ReactSelectOptions) => o.value === form.sourceName)

  const canAccessVerification = useAppSelector(FeaturePermissions.canAccessVerification);
  const isCombinedReport = useMemo(() => surveyData.type === SurveyType.Aggregation, [surveyData]);

  const checkOptions = getCheckOptions(canAccessVerification);

  const handleChange = ({ option, code } : OnChangeReportingProps) => {
    return updateForm({ code, value: option?.value });
  }

  return <>
    <div className='mt-4'>
      <h5>Survey name</h5>
      <Input disabled={isDisabled} placeholder={getSurveyName(surveyData, initiative)} onChange={(el) => updateForm({ code: 'name', value: el.target.value })} defaultValue={form.name} />
    </div>

    {isUserStaff ?
      <FormGroup className='mt-4' disabled={errorState === ERRORS.REPORT_LIMIT}>
        <h5>Survey Version</h5>

        <div className='mt-2 row'>
          <div className='col-12 col-md-6'>
            <Select
              isDisabled={isDisabled}
              placeholder={'Survey Version'}
              className='w-100 z-4'
              onChange={(option: { value: Blueprints } | null) => updateForm({ code: 'sourceName', value: option?.value })}
              value={selectedSurveyVersion}
              options={surveyVersionOptions}
            />
          </div>
        </div>
      </FormGroup>
      : null}

      <SurveyReportingSettings
        formDisabled={errorState === ERRORS.REPORT_LIMIT}
        isDisabled={isDisabled}
        form={form}
        onChange={handleChange}
      />

    {isCombinedReport ? null : (
      <SurveyQuestionForm
        disabled={errorState === ERRORS.REPORT_LIMIT}
        isCheckboxDisabled={isDisabled}
        checkOptions={checkOptions}
        form={{...form, verificationRequired: canAccessVerification && form.verificationRequired}}
        updateForm={updateForm}
      />
    )}
  </>
}
